import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./css/datepicker-custom.css";

const AvailabilityCard = ({
  onSearch,
  initialStart,
  initialEnd,
  initialGuests,
  listingId,
}) => {
  const [startDate, setStartDate] = useState(initialStart || null);
  const [endDate, setEndDate] = useState(initialEnd || null);
  const [guests, setGuests] = useState(initialGuests || 1);
  const [showDate, setShowDate] = useState(false);
  const [showGuests, setShowGuests] = useState(false);
  const [checking, setChecking] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const cardRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setShowDate(false);
        setShowGuests(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleRange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const formatDate = (d) => (d ? d.toISOString().slice(0, 10) : "");

  const handleSearch = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setChecking(true);

    if (!startDate || !endDate) {
      setErrorMsg("נא לבחור תאריכים");
      setChecking(false);
      return;
    }

    const payload = { startDate, endDate, guests };
    let handledByParent = false;
    if (onSearch) {
      try {
        const result = await onSearch(payload);
        handledByParent = result !== false;
      } catch (err) {
        console.error("onSearch failed", err);
        handledByParent = true;
      }
    }

    if (handledByParent) {
      setChecking(false);
      return;
    }

    const params = new URLSearchParams({
      listingId: listingId || "",
      checkIn: formatDate(startDate),
      checkOut: formatDate(endDate),
      guests: guests || 1,
    });

    // בדיקת זמינות ב-API
    if (listingId) {
      try {
        const availabilityUrl = new URL(
          "https://seasideeilat.co.il/wp-json/guesty/v1/search-available",
        );
        availabilityUrl.search = params.toString();
        const res = await fetch(availabilityUrl.toString());
        const data = await res.json();
        console.log(data);

        if (!data.success || !Array.isArray(data.listings)) {
          setErrorMsg("שגיאה בבדיקת זמינות");
          setChecking(false);
          return;
        }

        const isAvailable = data.listings.some((l) => l._id === listingId);

        if (!isAvailable) {
          setErrorMsg("הווילה לא זמינה, אנא בחר תאריכים אחרים.");
          setChecking(false);
          return;
        }
        const target = new URL("/booking", window.location.origin);
        target.search = params.toString();
        window.location.href = target.toString();
      } catch (err) {
        console.error("availability check failed", err);
        setErrorMsg("שגיאה בבדיקת זמינות, נסה שוב.");
        setChecking(false);
        return;
      }
    }

    setChecking(false);
  };

  return (
    <form
      className="avail-card"
      onSubmit={handleSearch}
      ref={cardRef}
      dir="rtl"
    >
      <h5 className="avail-title">חיפוש זמינות</h5>

      <div className="avail-field-wrap">
        <div className="avail-field" onClick={() => setShowDate((s) => !s)}>
          <div className="icon bi bi-calendar3"></div>
          <div className="grow">
            <div className="placeholder">תאריך התחלה - תאריך סיום</div>
            <div className="value">
              {startDate
                ? `${startDate.toLocaleDateString("he-IL")} - ${
                    endDate ? endDate.toLocaleDateString("he-IL") : "בחר סיום"
                  }`
                : "בחר טווח תאריכים"}
            </div>
          </div>
        </div>

        <div
          className={`avail-pop avail-datepicker ${showDate ? "show" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <DatePicker
            inline
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleRange}
            minDate={new Date()}
            dateFormat="dd/MM/yyyy"
            locale="he"
          />
          <button
            type="button"
            className="avail-apply"
            onClick={() => setShowDate(false)}
          >
            אישור
          </button>
        </div>
      </div>

      <div className="avail-field-wrap">
        <div
          className="avail-field"
          onClick={() => setShowGuests((s) => !s)}
          role="button"
        >
          <i className="icon bi bi-person"></i>
          <div className="grow">
            <div className="placeholder">אורחים</div>
            <div className="value">
              {guests > 0
                ? `${guests} אורח${guests > 1 ? "ים" : ""}`
                : "אורחים"}
            </div>
          </div>
        </div>

        <div
          className={`avail-pop avail-guests ${showGuests ? "show" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="guest-row">
            <div className="label">אורחים</div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="icon-btn"
                onClick={() => setGuests((g) => Math.max(0, g - 1))}
                aria-label="הפחת אורחים"
              >
                <i className="bi bi-dash-circle"></i>
              </button>
              <span className="guest-count">{guests}</span>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setGuests((g) => g + 1)}
                aria-label="הוסף אורחים"
              >
                <i className="bi bi-plus-circle"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <button type="submit" className="avail-btn">
        {checking ? "בודק זמינות..." : "בדיקת הצעת מחיר"}
      </button>
      {errorMsg && <div className="avail-error">{errorMsg}</div>}
    </form>
  );
};

export default AvailabilityCard;
